import { getApiClient } from '../../api/client';
import type { components } from '../../api/schema';

export type VoiceAgentDashboard = components['schemas']['VoiceAgentDashboardRead'];
export type VoiceAgentRevision = components['schemas']['VoiceAgentRevisionRead'];
export type VoiceAgentCall = components['schemas']['VoiceAgentCallRead'];
export type VoiceAgentScorecard = components['schemas']['VoiceAgentScorecardRead'];
export type VoiceAgentScriptPreview = components['schemas']['VoiceAgentScriptPreviewRead'];

export type VoiceAgentOption = {
  id: string;
  name?: string;
  category?: string;
  preview_url?: string | null;
  deprecated?: boolean;
};

export type VoiceAgentOptions = {
  voices: VoiceAgentOption[];
  llms: VoiceAgentOption[];
  tts_models: VoiceAgentOption[];
};

function errorMessage(prefix: string, error: unknown): Error {
  const detail =
    typeof error === 'object' &&
    error !== null &&
    'detail' in error &&
    typeof error.detail === 'string'
      ? error.detail
      : null;
  return new Error(detail ? `${prefix}: ${detail}` : `${prefix}: ${JSON.stringify(error)}`);
}

export async function getVoiceAgentDashboard(): Promise<VoiceAgentDashboard> {
  const client = getApiClient();
  const { data, error } = await client.GET('/voice-agent');
  if (error) throw errorMessage('Failed to load the voice agent', error);
  if (!data) throw new Error('Failed to load the voice agent: empty response');
  return data;
}

export async function syncVoiceAgent(): Promise<VoiceAgentDashboard> {
  const client = getApiClient();
  const { data, error } = await client.POST('/voice-agent/sync');
  if (error) throw errorMessage('Failed to sync with ElevenLabs', error);
  if (!data) throw new Error('Failed to sync with ElevenLabs: empty response');
  return data;
}

export async function saveVoiceAgentRevision(
  config: Record<string, unknown>,
  expectedProviderVersionId: string,
  changeNote: string,
): Promise<VoiceAgentRevision> {
  const client = getApiClient();
  const { data, error } = await client.POST('/voice-agent/revisions', {
    body: {
      config,
      expected_provider_version_id: expectedProviderVersionId,
      change_note: changeNote,
    },
  });
  if (error) throw errorMessage('Failed to save the Testing revision', error);
  if (!data) throw new Error('Failed to save the Testing revision: empty response');
  return data;
}

export async function promoteVoiceAgentRevision(
  revisionUuid: string,
  changeNote: string,
): Promise<VoiceAgentRevision> {
  const client = getApiClient();
  const { data, error } = await client.POST('/voice-agent/revisions/{revision_uuid}/promote', {
    params: { path: { revision_uuid: revisionUuid } },
    body: { change_note: changeNote },
  });
  if (error) throw errorMessage('Failed to promote the Testing revision', error);
  if (!data) throw new Error('Failed to promote the Testing revision: empty response');
  return data;
}

export async function getVoiceAgentOptions(): Promise<VoiceAgentOptions> {
  const client = getApiClient();
  const { data, error } = await client.GET('/voice-agent/options');
  if (error) throw errorMessage('Failed to load ElevenLabs options', error);
  const options = data as Record<string, unknown[]> | undefined;
  return {
    voices: (options?.voices ?? []) as VoiceAgentOption[],
    llms: (options?.llms ?? []) as VoiceAgentOption[],
    tts_models: (options?.tts_models ?? []) as VoiceAgentOption[],
  };
}

export async function listVoiceAgentScorecards(
  query?: string,
): Promise<VoiceAgentScorecard[]> {
  const client = getApiClient();
  const { data, error } = await client.GET('/voice-agent/scorecards', {
    params: { query: { limit: 100, ...(query ? { q: query } : {}) } },
  });
  if (error) throw errorMessage('Failed to load scorecards', error);
  return data ?? [];
}

export async function getVoiceAgentScorecardPreview(
  reportId: number,
): Promise<VoiceAgentScriptPreview> {
  const client = getApiClient();
  const { data, error } = await client.GET('/voice-agent/scorecards/{report_id}/preview', {
    params: { path: { report_id: reportId } },
  });
  if (error) throw errorMessage('Failed to preview the Testing script', error);
  if (!data) throw new Error('Failed to preview the Testing script: empty response');
  return data;
}

export async function listVoiceAgentRevisionCalls(
  revisionUuid: string,
): Promise<VoiceAgentCall[]> {
  const client = getApiClient();
  const { data, error } = await client.GET('/voice-agent/revisions/{revision_uuid}/calls', {
    params: {
      path: { revision_uuid: revisionUuid },
      query: { limit: 100 },
    },
  });
  if (error) throw errorMessage('Failed to load revision calls', error);
  return data ?? [];
}
